import type {
  AttentionItem,
  EventRequirement,
  OperationEvent,
  OperationsSnapshot,
  TaskStatus,
  WorkBlock,
  WorkClassification,
  WorkTask,
} from "./operations";
import { workClassifications } from "./operations";

export function eventById(snapshot: OperationsSnapshot, eventId: string): OperationEvent | undefined {
  return snapshot.events.find((event) => event.id === eventId);
}

export function staffByIdMap(snapshot: OperationsSnapshot): Record<string, OperationsSnapshot["staff"][number]> {
  return Object.fromEntries(snapshot.staff.map((staff) => [staff.id, staff]));
}

export function taskByIdMap(snapshot: OperationsSnapshot): Record<string, WorkTask> {
  return Object.fromEntries(snapshot.tasks.map((task) => [task.id, task]));
}

export function isCancelled(event: OperationEvent | undefined): boolean {
  return event?.status === "cancelled";
}

/** Cancelled Events keep history but do not participate in active Operations. */
export function isActiveInOperations(event: OperationEvent | undefined): boolean {
  return Boolean(event) && event!.status !== "cancelled";
}

export function tasksForEvent(snapshot: OperationsSnapshot, eventId: string): WorkTask[] {
  return snapshot.tasks.filter((task) => task.eventId === eventId);
}

export function activeOperationsTasks(snapshot: OperationsSnapshot): WorkTask[] {
  const events = Object.fromEntries(snapshot.events.map((event) => [event.id, event]));
  return snapshot.tasks.filter((task) => isActiveInOperations(events[task.eventId]));
}

export function activeAttention(snapshot: OperationsSnapshot): AttentionItem[] {
  const events = Object.fromEntries(snapshot.events.map((event) => [event.id, event]));
  return snapshot.attention.filter((item) => !item.resolved && isActiveInOperations(events[item.eventId]));
}

export function visibleBlocks(snapshot: OperationsSnapshot): WorkBlock[] {
  const activeIds = new Set(activeOperationsTasks(snapshot).map((task) => task.id));
  return snapshot.blocks.filter((block) => block.taskIds.some((id) => activeIds.has(id)));
}

export function visibleTasksInBlock(snapshot: OperationsSnapshot, block: WorkBlock): WorkTask[] {
  const active = new Set(activeOperationsTasks(snapshot).map((task) => task.id));
  const byId = taskByIdMap(snapshot);
  return block.taskIds.map((id) => byId[id]).filter((task): task is WorkTask => Boolean(task) && active.has(task.id));
}

export function classificationProgress(tasks: WorkTask[], classification: WorkClassification): { complete: number; total: number; percent: number } {
  const scoped = tasks.filter((task) => task.classification === classification);
  const total = scoped.length;
  const complete = scoped.filter((task) => task.status === "complete").length;
  return { complete, total, percent: total === 0 ? 0 : Math.round((complete / total) * 100) };
}

export function executionPercents(tasks: WorkTask[]): Record<WorkClassification, number> {
  return Object.fromEntries(workClassifications.map((classification) => [classification, classificationProgress(tasks, classification).percent])) as Record<WorkClassification, number>;
}

export function isEventReady(event: OperationEvent): boolean {
  const critical = event.requirements.filter((requirement) => requirement.critical);
  if (critical.length === 0) return event.requirements.every((requirement) => requirement.resolved);
  return critical.every((requirement) => requirement.resolved);
}

export function isOperationallyComplete(tasks: WorkTask[]): boolean {
  return tasks.length > 0 && tasks.every((task) => task.status === "complete");
}

export function unresolvedCriticalRequirements(event: OperationEvent): EventRequirement[] {
  return event.requirements.filter((requirement) => requirement.critical && !requirement.resolved);
}

export function todaySummary(snapshot: OperationsSnapshot) {
  const tasks = activeOperationsTasks(snapshot);
  const eventIds = new Set(tasks.map((task) => task.eventId));
  const events = snapshot.events.filter((event) => eventIds.has(event.id));
  const happeningToday = events.filter((event) => event.status === "confirmed" && /today/i.test(event.dateLabel)).length;
  const locations = new Set(visibleBlocks(snapshot).map((block) => block.location));
  const outstandingReset = tasks.filter((task) => task.classification === "reset" && task.status !== "complete").length;
  const resetEventIds = new Set(tasks.filter((task) => task.classification === "reset" && task.status !== "complete").map((task) => task.eventId));

  return {
    eventsAffectingToday: events.length,
    happeningToday,
    workBlocks: visibleBlocks(snapshot).length,
    locations: locations.size,
    eligibleTasks: tasks.filter((task) => task.status === "eligible").length,
    scheduledTasks: tasks.filter((task) => task.status === "scheduled").length,
    attentionCount: activeAttention(snapshot).length,
    blockingAttention: activeAttention(snapshot).filter((item) => item.kind === "blocker").length,
    outstandingReset,
    resetSourceEvents: resetEventIds.size,
  };
}

const focusPriority: TaskStatus[] = ["in_progress", "scheduled", "eligible", "blocked"];

export function focusTaskForNow(snapshot: OperationsSnapshot): WorkTask | undefined {
  const now = visibleBlocks(snapshot)[0];
  if (!now) return undefined;
  const tasks = visibleTasksInBlock(snapshot, now).filter((task) => task.status !== "complete");
  for (const status of focusPriority) {
    const match = tasks.find((task) => task.status === status);
    if (match) return match;
  }
  return undefined;
}

export function nextTasksForMobile(snapshot: OperationsSnapshot, focusId?: string, limit = 2): WorkTask[] {
  const tasks = activeOperationsTasks(snapshot).filter((task) => task.id !== focusId && task.status !== "complete" && task.classification !== "reset");
  const ranked = [...tasks].sort((a, b) => focusPriority.indexOf(a.status) - focusPriority.indexOf(b.status));
  return ranked.slice(0, limit);
}
